import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Badge, Alert, Table, Spinner, Form, Modal, Tabs, Tab, Row, Col } from 'react-bootstrap';
import { FaCar } from 'react-icons/fa';
import { API_URL } from './config';

/**
 * Admin queue for XGO's Drive to Own (rent-to-buy) program — real, live-wired (2026-09-15, see
 * xgo_rideshare_backend's Documentation/KNOWN_ISSUES_AND_FIX_BACKLOG.md #71). A driver applies
 * for a specific vehicle+repayment plan (built in xgo_web_app/xgo_Life App); this screen is where
 * an admin approves/rejects, and — once the vehicle is physically handed over — marks it
 * collected, which is what actually starts the repayment clock and activates the driver's
 * "drive_to_own" ownership tier (see VehicleOwnershipSettings.js for the tier system itself).
 *
 * Kept as its own page rather than a tab on VehicleOwnershipSettings.js — that's a settings form,
 * this is an approval queue/table, different enough shapes that combining them would cramp both.
 * Has its own two internal tabs instead: Applications (this queue) and Plans (add a vehicle+terms
 * to the catalog applicants choose from — added 2026-09-16, since there was previously no admin UI
 * for this at all, only a one-off seed migration).
 */
const STATUS_OPTIONS = ['all', 'pending', 'approved', 'rejected', 'collected'];

const STATUS_BADGE = {
  pending: 'warning',
  approved: 'info',
  rejected: 'danger',
  collected: 'success',
};

const EMPTY_PLAN_FORM = {
  vehicle_make: '',
  vehicle_model: '',
  vehicle_value: '',
  deposit_amount: '',
  weekly_repayment: '',
  term_years: '',
};

const WEEKS_PER_YEAR = 52;

const DriveToOwnApplications = () => {
  const [statusFilter, setStatusFilter] = useState('pending');
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [actingId, setActingId] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [planForm, setPlanForm] = useState(EMPTY_PLAN_FORM);
  const [planMessage, setPlanMessage] = useState(null);
  const [creatingPlan, setCreatingPlan] = useState(false);

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    try {
      const qs = statusFilter === 'all' ? '' : `?status=${statusFilter}`;
      const res = await fetch(`${API_URL}/drive-to-own/applications${qs}`);
      const data = await res.json();
      setApplications(data.applications || []);
    } catch (e) {
      setMessage({ type: 'danger', text: 'Failed to load applications.' });
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const fetchPlans = useCallback(async () => {
    setLoadingPlans(true);
    try {
      const res = await fetch(`${API_URL}/drive-to-own/plans`);
      const data = await res.json();
      setPlans(data.plans || []);
    } catch (e) {
      setPlanMessage({ type: 'danger', text: 'Failed to load plans.' });
    } finally {
      setLoadingPlans(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const updatePlanField = (field, value) => {
    setPlanForm((prev) => ({ ...prev, [field]: value }));
  };

  const createPlan = async (e) => {
    e.preventDefault();
    setCreatingPlan(true);
    setPlanMessage(null);
    try {
      const res = await fetch(`${API_URL}/drive-to-own/plans`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vehicle_make: planForm.vehicle_make,
          vehicle_model: planForm.vehicle_model,
          vehicle_value: Number(planForm.vehicle_value),
          deposit_amount: Number(planForm.deposit_amount),
          weekly_repayment: Number(planForm.weekly_repayment),
          // Admin enters the term in years; the API wants weeks — converted here rather than
          // asking the admin to do the arithmetic themselves.
          term_weeks: Math.round(Number(planForm.term_years) * WEEKS_PER_YEAR),
        }),
      });
      const data = await res.json();
      if (!res.ok || data.status !== 'success') throw new Error(data.message || 'Failed to add plan');
      setPlanMessage({ type: 'success', text: `${planForm.vehicle_make} ${planForm.vehicle_model} added to the catalog.` });
      setPlanForm(EMPTY_PLAN_FORM);
      fetchPlans();
    } catch (err) {
      setPlanMessage({ type: 'danger', text: err.message });
    } finally {
      setCreatingPlan(false);
    }
  };

  const approve = async (id) => {
    setActingId(id);
    setMessage(null);
    try {
      const res = await fetch(`${API_URL}/drive-to-own/applications/${id}/approve`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_id: 'dashboard-admin' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to approve');
      setMessage({ type: 'success', text: `Application #${id} approved.` });
      fetchApplications();
    } catch (e) {
      setMessage({ type: 'danger', text: e.message });
    } finally {
      setActingId(null);
    }
  };

  const submitReject = async () => {
    if (!rejectTarget) return;
    setActingId(rejectTarget);
    setMessage(null);
    try {
      const res = await fetch(`${API_URL}/drive-to-own/applications/${rejectTarget}/reject`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_id: 'dashboard-admin', reason: rejectReason }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to reject');
      setMessage({ type: 'success', text: `Application #${rejectTarget} rejected.` });
      setRejectTarget(null);
      setRejectReason('');
      fetchApplications();
    } catch (e) {
      setMessage({ type: 'danger', text: e.message });
    } finally {
      setActingId(null);
    }
  };

  const markCollected = async (id) => {
    setActingId(id);
    setMessage(null);
    try {
      const res = await fetch(`${API_URL}/drive-to-own/applications/${id}/collect`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_id: 'dashboard-admin' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to mark collected');
      setMessage({ type: 'success', text: `Application #${id} marked collected — repayment clock started.` });
      fetchApplications();
    } catch (e) {
      setMessage({ type: 'danger', text: e.message });
    } finally {
      setActingId(null);
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <h3><FaCar style={{ marginRight: 8 }} />Drive to Own</h3>

      <Tabs defaultActiveKey="applications" className="mb-3">
        <Tab eventKey="applications" title="Applications">
          <p className="text-muted mt-3">
            Review applications, approve or reject, then mark a vehicle collected once it's physically
            handed over — that's the step that starts repayment and activates the driver's tier.
          </p>

          <Form.Group className="mb-3" style={{ maxWidth: 240 }}>
            <Form.Label>Filter by status</Form.Label>
            <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>{s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </Form.Select>
          </Form.Group>

          {message && <Alert variant={message.type} dismissible onClose={() => setMessage(null)}>{message.text}</Alert>}

          <Card>
            <Card.Body>
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : applications.length === 0 ? (
                <p className="text-muted mb-0">No applications for this filter.</p>
              ) : (
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Driver</th>
                      <th>Applicant</th>
                      <th>Vehicle</th>
                      <th>Deposit</th>
                      <th>Weekly</th>
                      <th>Term</th>
                      <th>Applied</th>
                      <th>Status</th>
                      <th>Progress</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {applications.map((app) => (
                      <tr key={app.id}>
                        <td>{app.id}</td>
                        <td>{app.driver_id}</td>
                        <td>
                          {app.applicant_name}
                          <br />
                          <small className="text-muted">{app.applicant_phone} · {app.license_years}yr license</small>
                        </td>
                        <td>{app.vehicle_make} {app.vehicle_model}</td>
                        <td>${Number(app.deposit_amount).toFixed(2)}</td>
                        <td>${Number(app.weekly_repayment).toFixed(2)}</td>
                        <td>{app.term_weeks} wk</td>
                        <td>{app.applied_at ? new Date(app.applied_at).toLocaleDateString() : '—'}</td>
                        <td><Badge bg={STATUS_BADGE[app.status] || 'secondary'}>{app.status}</Badge></td>
                        <td>
                          {app.status === 'collected' && app.remaining_balance != null
                            ? `$${Number(app.remaining_balance).toFixed(2)} left`
                            : '—'}
                        </td>
                        <td>
                          {app.status === 'pending' && (
                            <>
                              <Button
                                size="sm" variant="success" className="me-2"
                                disabled={actingId === app.id}
                                onClick={() => approve(app.id)}
                              >
                                Approve
                              </Button>
                              <Button
                                size="sm" variant="outline-danger"
                                disabled={actingId === app.id}
                                onClick={() => setRejectTarget(app.id)}
                              >
                                Reject
                              </Button>
                            </>
                          )}
                          {app.status === 'approved' && (
                            <Button
                              size="sm" variant="primary"
                              disabled={actingId === app.id}
                              onClick={() => markCollected(app.id)}
                            >
                              Mark Collected
                            </Button>
                          )}
                          {(app.status === 'rejected' || app.status === 'collected') && (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="plans" title="Plans">
          <p className="text-muted mt-3">
            Add a specific vehicle and its repayment terms to the catalog drivers apply against.
            Repayments are weekly.
          </p>

          {planMessage && (
            <Alert variant={planMessage.type} dismissible onClose={() => setPlanMessage(null)}>
              {planMessage.text}
            </Alert>
          )}

          <Card className="mb-4">
            <Card.Body>
              <Card.Title style={{ fontSize: '1rem' }}>Add a vehicle plan</Card.Title>
              <Form onSubmit={createPlan}>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Make</Form.Label>
                      <Form.Control
                        required
                        value={planForm.vehicle_make}
                        onChange={(e) => updatePlanField('vehicle_make', e.target.value)}
                        placeholder="e.g. Nissan"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Model</Form.Label>
                      <Form.Control
                        required
                        value={planForm.vehicle_model}
                        onChange={(e) => updatePlanField('vehicle_model', e.target.value)}
                        placeholder="e.g. Dayz"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Vehicle value (USD)</Form.Label>
                      <Form.Control
                        required type="number" min="0" step="0.01"
                        value={planForm.vehicle_value}
                        onChange={(e) => updatePlanField('vehicle_value', e.target.value)}
                      />
                      <Form.Text muted>
                        Reference only (e.g. market price) — not used to calculate what the driver
                        owes. The real amount financed is the deposit plus weekly repayment × term.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Deposit (USD)</Form.Label>
                      <Form.Control
                        required type="number" min="0" step="0.01"
                        value={planForm.deposit_amount}
                        onChange={(e) => updatePlanField('deposit_amount', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Weekly repayment (USD)</Form.Label>
                      <Form.Control
                        required type="number" min="0" step="0.01"
                        value={planForm.weekly_repayment}
                        onChange={(e) => updatePlanField('weekly_repayment', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Term (years)</Form.Label>
                      <Form.Control
                        required type="number" min="0.1" step="0.5"
                        value={planForm.term_years}
                        onChange={(e) => updatePlanField('term_years', e.target.value)}
                      />
                      <Form.Text muted>
                        {planForm.term_years
                          ? `≈ ${Math.round(Number(planForm.term_years) * WEEKS_PER_YEAR)} weeks`
                          : 'Converted to weeks automatically when saved.'}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                <Button type="submit" variant="primary" className="mt-3" disabled={creatingPlan}>
                  {creatingPlan ? 'Adding…' : 'Add plan'}
                </Button>
              </Form>
            </Card.Body>
          </Card>

          <Card>
            <Card.Body>
              <Card.Title style={{ fontSize: '1rem' }}>Active plans</Card.Title>
              {loadingPlans ? (
                <Spinner animation="border" size="sm" />
              ) : plans.length === 0 ? (
                <p className="text-muted mb-0">No plans yet — add one above.</p>
              ) : (
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Vehicle</th>
                      <th>Value</th>
                      <th>Deposit</th>
                      <th>Weekly</th>
                      <th>Term</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plans.map((plan) => (
                      <tr key={plan.id}>
                        <td>{plan.vehicle_make} {plan.vehicle_model}</td>
                        <td>${Number(plan.vehicle_value).toFixed(2)}</td>
                        <td>${Number(plan.deposit_amount).toFixed(2)}</td>
                        <td>${Number(plan.weekly_repayment).toFixed(2)}</td>
                        <td>{plan.term_weeks} wk</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      <Modal show={!!rejectTarget} onHide={() => setRejectTarget(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Reject application #{rejectTarget}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Reason (optional)</Form.Label>
            <Form.Control
              as="textarea" rows={3} value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setRejectTarget(null)}>Cancel</Button>
          <Button variant="danger" onClick={submitReject} disabled={actingId === rejectTarget}>
            Reject application
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DriveToOwnApplications;
